import { Component, DestroyRef, inject, Inject, Input, OnInit } from '@angular/core';
import { ArivoStepperComponent } from '@shared/components/ui-elements';
import { FormBuilder, Validators } from '@angular/forms';
import { ContractCreateResolve, ContractLicensePlate, ContractTemplate, Garage, LicensePlate, ProviderLinkOptions } from '@shared/models';
import { ContractCreateService, ContractTemplateInfo } from '../contract-create.service';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDateWithTimezone, getContractPriceAdjustmentIncreaseString, months, parseDate, setLoading, TypedRoute } from '@shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services';
import { ContractsApiService } from '../../contracts-api.service';
import { debounceTime, pairwise } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LocalizedCurrencyPipe, LocalizedPercentagePipe } from '@core/pipes';
import {
  ContractCreateCancelled,
  ContractCreateLicensePlateAdded,
  ContractCreateLicensePlateRemoved,
  ContractCreateSaved,
} from '@integrations/amplitude';

@Component({
  selector: 'app-contract-create-second-step',
  templateUrl: './contract-create-second-step.component.html',
  styleUrl: './contract-create-second-step.component.scss',
})
export class ContractCreateSecondStepComponent implements OnInit {
  @Input() stepperComponent!: ArivoStepperComponent;
  destroyRef = inject(DestroyRef);

  garage?: Garage;
  product?: ContractTemplate;
  licensePlates: ContractLicensePlate[] = [];
  contractTemplateInfo: ContractTemplateInfo | null = null;
  loading = false;
  providerLinks!: ProviderLinkOptions;
  licensePlatesInitialized = false;

  constructor(
    private router: Router,
    private _formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private contractsApiService: ContractsApiService,
    private contractCreateService: ContractCreateService,
    private providerSettingsService: ProviderSettingsService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private localizedPercentagePipe: LocalizedPercentagePipe,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
    @Inject(ActivatedRoute) private route: TypedRoute<ContractCreateResolve>,
  ) {}

  ngOnInit() {
    const licensePlates: ContractLicensePlate[] = this.route.snapshot.data.contractCreateApiResponse.license_plates.map(
      (licensePlate: LicensePlate): ContractLicensePlate => {
        return {
          ...licensePlate,
          enabled: false,
        };
      },
    );

    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });

    this.contractCreateService.garageSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((garage) => {
      if (this.garage !== garage) {
        this.garage = garage;

        this.licensePlates = licensePlates.map((licensePlate: LicensePlate): ContractLicensePlate => {
          return {
            ...licensePlate,
            enabled: false,
          };
        });

        this.contractForm.reset();
        this.contractForm.controls.licensePlates.reset();
        this.contractForm.controls.start.patchValue(new Date());
        this.contractForm.controls.parkingSpots.patchValue(1);
        this.contractForm.controls.licensePlates.patchValue([]);
      }
    });

    this.contractCreateService.productSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((product) => {
      this.product = product;
      if (this.product.initial_start && parseDate(this.product.initial_start) > new Date()) {
        this.contractForm.controls.start.patchValue(parseDate(this.product.initial_start));
      }
      this.updateContractTemplateInfo();
    });

    this.contractForm.valueChanges
      .pipe(pairwise(), debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(([oldVal, newVal]) => {
        if (oldVal && this.product && (oldVal.start !== newVal.start || oldVal.parkingSpots !== newVal.parkingSpots)) {
          this.updateContractTemplateInfo();
        }
      });
  }

  get totalPrice(): number {
    return this.contractTemplateInfo?.price.amount ?? 0;
  }

  contractForm = this._formBuilder.group({
    start: [new Date(), Validators.required],
    parkingSpots: [1, Validators.required],
    licensePlates: [<Array<LicensePlate>>[]],
    termsAndConditions: [false, [Validators.requiredTrue]],
  });

  startDatepickerFilter = (d: Date | null): boolean => {
    if (this.product?.initial_start && parseDate(this.product.initial_start) > new Date()) {
      const date = d || parseDate(this.product.initial_start);
      return parseDate(this.product.initial_start).setHours(0, 0, 0, 0) <= date.valueOf();
    } else {
      const date = d || new Date();
      return new Date().setHours(0, 0, 0, 0) <= date.valueOf();
    }
  };

  updateLicensePlates(licensePlates: LicensePlate[]) {
    if (!this.licensePlatesInitialized) {
      this.licensePlatesInitialized = true;
    } else {
      this.trackLicensePlateChangesToAmplitude(this.contractForm.controls.licensePlates.value ?? [], licensePlates);
    }
    this.contractForm.controls.licensePlates.patchValue(licensePlates);
    this.contractForm.controls.licensePlates.updateValueAndValidity();
  }

  updateContractTemplateInfo() {
    if (!this.product) {
      return;
    }
    this.contractCreateService
      .getContractTemplateInfo(
        this.product.id,
        this.contractForm.value.parkingSpots!,
        formatDateWithTimezone(this.contractForm.value.start!),
      )
      .pipe(setLoading(this))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contractTemplateInfo) => {
        this.contractTemplateInfo = contractTemplateInfo;
      });
  }

  get isFirstOfMonth(): boolean {
    const startDate = this.contractForm.get('start')?.value;
    return startDate ? startDate.getDate() === 1 : false;
  }

  get proratedPrice(): number {
    return this.contractTemplateInfo?.first_payment_period.price.amount ?? 0;
  }

  get increasedPrice(): number {
    return this.contractTemplateInfo?.increased_price?.amount ?? 0;
  }

  get vatPercent(): number {
    return this.contractTemplateInfo?.vat_percent ?? 0;
  }

  back() {
    this.amplitudeEventService.trackEvent(new ContractCreateCancelled({ contractType: 'contract' }));
    this.stepperComponent.previous();
  }

  finish() {
    this.contractForm.markAllAsTouched();
    this.contractForm.controls.licensePlates.updateValueAndValidity();

    const licensePlates = this.contractForm.value.licensePlates;

    if (this.contractForm.valid && this.garage && this.product) {
      const contract = {
        contract_template_id: this.product.id,
        group_limit: this.contractForm.value.parkingSpots!,
        license_plates: licensePlates!,
        start: formatDateWithTimezone(this.contractForm.value.start!),
        end: null,
      };

      this.contractsApiService
        .createContract(contract)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.amplitudeEventService.trackEvent(new ContractCreateSaved({ contractType: 'contract' }));
            this.router.navigateByUrl('/dashboard').finally(() => {
              this.toastService.add({
                message: this.translateService.instant('Contracts.CreateContractSuccess'),
                type: 'success',
              });
            });
          },
          error: (error) => {
            if (error.status === 400 && error.error.detail) {
              const detail = error.error.detail;
              for (const key in detail) {
                if (detail.hasOwnProperty(key)) {
                  detail[key].forEach((message: string) => {
                    this.toastService.add({
                      message: message,
                      type: 'error',
                    });
                  });
                }
              }
            } else {
              this.toastService.add({
                message: this.translateService.instant('Contracts.UpdateContractError'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  getPriceIncreaseString(contractTemplate: ContractTemplate): string | null {
    return getContractPriceAdjustmentIncreaseString(
      contractTemplate,
      'Contracts.PriceIncreaseInfoCreate',
      this.translateService,
      this.localizedCurrencyPipe,
      this.localizedPercentagePipe,
    );
  }

  getIncreasedPriceString(contractTemplate: ContractTemplate): string | null {
    let translationPlurality;
    if (!contractTemplate.price_adjustment) {
      return null;
    }
    translationPlurality = contractTemplate.price_adjustment.month_start === contractTemplate.price_adjustment.month_end ? 'Singular' : 'Plural';
    return this.translateService.instant(`Contracts.IncreasedPriceInfoCreate.${translationPlurality}`, {
      start: this.translateService.instant('Common.MonthNames.' + months[contractTemplate.price_adjustment.month_start - 1]),
      end: this.translateService.instant('Common.MonthNames.' + months[contractTemplate.price_adjustment.month_end - 1]),
    });
  }

  get showVpi(): boolean {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }

  trackLicensePlateChangesToAmplitude(previousLicensePlates: LicensePlate[], licensePlates: LicensePlate[]): void {
    let previousEnabledLicensePlates = previousLicensePlates.length;
    let enabledLicensePlates = licensePlates.length;
    let maxEnabledLicensePlates = this.contractForm.value.parkingSpots ?? 0;

    const payload = this.amplitudeEventService.buildContractParkingSpacesPayload(maxEnabledLicensePlates, enabledLicensePlates);

    if (previousEnabledLicensePlates < enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractCreateLicensePlateAdded(payload));
    } else if (previousEnabledLicensePlates > enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractCreateLicensePlateRemoved(payload));
    }
  }
}
